import { addDays, addMinutes, startOfDay } from 'date-fns';
import _ from 'lodash';
import {
    type ComputeAvailabilityReqProductsItemDto,
    GetEventResBookingsItem0ProductOptionsItemDto,
    TimezoneName,
} from '../../generated';

import {
    AvailabilityFilterValues,
    AvailableProduct,
} from './availability.types';
import { convertDurationDtoToDuration } from '../common.converters';
import { Period } from '../common.types';
import { routeDtoToProductRoute } from '../product/product.converters';

export const getIntervalTimes = (date: Period) => {
    const times = [];
    let { start } = date;
    const end = date.end || date.start;
    while (start <= end) {
        times.push(start);
        start = addMinutes(start, 15);
    }

    return times;
};

export const availabilityProductDtoToAvailabilityProduct = ({
    product,
    financials,
}: Omit<any, 'events'>): AvailableProduct => ({
    id: product.id,
    type: product.type,
    name: product.name,
    autoscaled: product.configuration?.autoscaled,
    shared: product.configuration?.shared,
    route: product.route ? routeDtoToProductRoute(product.route) : undefined,
    capacity: {
        id: product.capacity?.id,
        name: product.capacity?.reference?.name,
        type: product.capacity?.reference?.type,
        default: product.capacity?.optimal,
    },
    schedule: {
        id: product.schedule?.id,
        name: product.schedule?.reference.name,
        type: product.schedule?.reference.type,
    },
    options: product.options?.items.map(
        (o: GetEventResBookingsItem0ProductOptionsItemDto) => ({
            id: o.id,
            name: o.name,
            type: o.type,
            pax: {},
            hasPrice: !!o.pricing,
            pricing: o?.pricing
                ? {
                      currency: o?.pricing?.currency.abbr,
                      price: o.pricing?.price.original,
                  }
                : undefined,
        })
    ),
    financials: {
        subtotal:
            financials?.subtotal.original || financials?.total.original || 0,
        total: financials?.total.original,
    },
    pricing: product.pricing
        ? {
              currency: product.pricing.currency.abbr,
              price: product.pricing.price.original,
          }
        : undefined,
    vacantSeats: product.capacity?.vacant,
    duration: product.schedule?.duration?.estimated
        ? convertDurationDtoToDuration(product.schedule.duration.estimated)
        : convertDurationDtoToDuration({ days: product.days?.length }),
    discount: product.discount,
});

export const filterToRequestBodyConverter = (
    filters: AvailabilityFilterValues
) => {
    const products: ComputeAvailabilityReqProductsItemDto[] = [];

    if (filters.product) {
        products.push({ id: filters.product.value });
    }

    if (filters.productTypes) {
        filters.productTypes.forEach(type => {
            products.push({ type });
        });
    }
    return {
        date:
            filters.dates && filters.dates.startDate && filters.dates.endDate
                ? {
                      // timezone: {
                      //     name: Intl.DateTimeFormat().resolvedOptions()
                      //         .timeZone as TimezoneName,
                      // },
                      timezone: {
                          name: Intl.DateTimeFormat().resolvedOptions()
                              .timeZone as TimezoneName,
                      },
                      start: Math.round(
                          filters.dates.startDate.getTime() / 1000
                      ),
                      end:
                          Math.round(filters.dates.endDate.getTime() / 1000) +
                          24 * 60 * 60 -
                          1,
                  }
                : {
                      // timezone: {
                      //     name: Intl.DateTimeFormat().resolvedOptions()
                      //         .timeZone as TimezoneName,
                      // },
                      timezone: {
                          name: Intl.DateTimeFormat().resolvedOptions()
                              .timeZone as TimezoneName,
                      },
                      start: Math.round(
                          startOfDay(new Date()).getTime() / 1000
                      ),
                      end:
                          Math.round(
                              addDays(startOfDay(new Date()), 7).getTime() /
                                  1000
                          ) +
                          24 * 60 * 60 -
                          1,
                  },
        pax: _.omitBy(filters.pax, v => !v),
        products: products.length > 0 ? products : undefined,
        pricing: filters.pricing
            ? {
                  currency: {
                      abbr: filters.pricing.currency || 'USD',
                      name: filters.pricing.currency || 'USD',
                  },
                  min_price: filters.pricing.minPrice,
                  max_price: filters.pricing.maxPrice,
              }
            : undefined,
        events: filters.events,
    };
};
